(() => {
  if (customElements.get('nordsign-contact-agent')) return;

  class NordsignContactAgent extends HTMLElement {
    connectedCallback() {
      if (this.initialized || !this.querySelector('[data-flow]')) return;
      this.initialized = true;
      this.copy = JSON.parse(this.querySelector('[data-contact-copy]').textContent);
      this.form = this.querySelector('form');
      this.body = this.querySelector('#ContactForm-body');
      this.email = this.querySelector('#ContactForm-email');
      this.phone = this.querySelector('#ContactForm-phone');
      this.messages = [];
      this.busy = false;
      this.endpoint = this.getEndpoint();
      this.agent = Boolean(this.endpoint);
      this.querySelector('[data-compose-actions]').hidden = false;
      this.querySelector('[data-starters]').hidden = false;
      this.querySelector('[data-reply]').hidden = !this.querySelector('[data-server-error]');
      if (this.agent) {
        this.querySelector('[data-identity]').textContent = this.copy.aiIdentity;
        this.querySelector('[data-ai-notice]').hidden = false;
        this.querySelector('[data-handoff]').hidden = false;
        this.querySelector('[data-methods]').hidden = false;
        this.querySelector('[data-continue-label]').textContent = this.copy.ask;
        const send = this.querySelector('[data-send]');
        send.type = 'button';
        send.addEventListener('click', event => this.submit(event));
        this.form.addEventListener('keydown', event => {
          if (event.key === 'Enter' && event.target.matches('input:not([type="radio"]):not([type="hidden"])')) {
            event.preventDefault();
            this.submit(event);
          }
        });
        this.form.addEventListener('submit', event => {
          event.preventDefault();
          event.stopImmediatePropagation();
        }, true);
      }
      this.querySelectorAll('[data-prompt]').forEach(button => {
        button.addEventListener('click', () => this.applyPrompt(button.dataset.prompt));
      });
      this.querySelector('[data-continue]').addEventListener('click', () => this.agent ? this.ask() : this.showReply());
      this.querySelector('[data-handoff]').addEventListener('click', () => this.showReply());
      this.querySelectorAll('input[type="radio"]').forEach(radio => radio.addEventListener('change', () => this.setMethod()));
      this.body.addEventListener('input', () => this.body.setCustomValidity(''));
      this.phone.addEventListener('input', () => this.phone.setCustomValidity(''));
      this.form.addEventListener('submit', event => this.submit(event));
      this.prefillProduct();
    }

    applyPrompt(prompt) {
      if (this.busy) return;
      const current = this.body.value;
      const prefixes = Array.from(this.querySelectorAll('[data-prompt]'), button => button.dataset.prompt);
      if (this.copy.productPrefix) prefixes.push(this.copy.productPrefix);
      const previous = prefixes.find(prefix => current.startsWith(prefix) || current.trim() === prefix.trim());
      const canReplace = !current.trim() || previous;
      const suffix = previous && current.startsWith(previous) ? current.slice(previous.length) : '';
      const next = prompt + suffix;
      if (canReplace && (this.body.maxLength < 0 || next.length <= this.body.maxLength)) {
        this.body.value = next;
        this.body.setCustomValidity('');
      }
      this.body.focus();
      this.body.setSelectionRange(this.body.value.length, this.body.value.length);
    }

    getEndpoint() {
      if (this.dataset.agentEnabled !== 'true') return null;
      const path = this.dataset.agentPath || '';
      if (!/^\/apps\/[a-z0-9-]+(?:\/[a-z0-9-]+)*\/?$/i.test(path)) return null;
      return path;
    }

    prefillProduct() {
      const fragment = new URLSearchParams(window.location.hash.slice(1));
      const query = new URLSearchParams(window.location.search);
      const product = (fragment.get('product') || query.get('product') || '').trim().slice(0, 200);
      if (!product) return;
      if (window.nordsignAssortmentPilot?.isBlockedContext(product)) { window.nordsignAssortmentPilot.blockEnquiry(this); return; }
      this.querySelector('[data-product]').value = product;
      const label = this.querySelector('[data-product-label]');
      label.textContent = this.copy.productContext + product;
      label.hidden = false;
      if (!this.body.value.trim()) this.body.value = this.copy.productPrefix + product + '. ';
    }

    validMessage(requireNew = false) {
      const present = Boolean(this.body.value.trim()) || (!requireNew && this.messages.some(message => message.role === 'user'));
      this.body.required = !this.agent || requireNew || !this.messages.length;
      this.body.setCustomValidity(present ? '' : this.copy.messageRequired);
      return this.body.reportValidity();
    }

    showReply() {
      if (this.busy || !this.validMessage()) return;
      this.querySelector('[data-reply]').hidden = false;
      if (!this.agent) this.querySelector('[data-compose-actions]').hidden = true;
      this.setMethod();
      this.querySelector('[data-reply-heading]').focus();
    }

    setMethod() {
      const phone = this.agent && this.querySelector('input[value="phone"]').checked;
      this.querySelector('[data-email-field]').hidden = phone;
      this.querySelector('[data-phone-field]').hidden = !phone;
      this.email.disabled = phone;
      this.email.required = !phone;
      this.phone.disabled = !phone;
      this.phone.required = phone;
      this.phone.setCustomValidity('');
    }

    setBusy(busy, status = '') {
      this.busy = busy;
      this.querySelectorAll('button').forEach(button => { button.disabled = busy; });
      this.querySelectorAll('input, textarea, summary').forEach(element => {
        if (element.matches('input:not([type="hidden"]):not([type="radio"]), textarea')) element.readOnly = busy;
        if (element.type === 'radio') element.disabled = busy;
      });
      this.querySelector('[data-status]').textContent = status;
      this.form.setAttribute('aria-busy', String(busy));
      this.querySelector('nordsign-file-upload')?.refreshControls?.();
    }

    error(message = '') {
      const output = this.querySelector('[data-error]');
      output.textContent = message;
      output.hidden = !message;
    }

    async request(payload) {
      const controller = new AbortController();
      this.controller = controller;
      const timeout = setTimeout(() => controller.abort(), 25000);
      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          signal: controller.signal,
          body: JSON.stringify({ version: 1, ...payload })
        });
        if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) throw new Error('Invalid response');
        return await response.json();
      } finally {
        clearTimeout(timeout);
        this.controller = null;
      }
    }

    async ask() {
      if (this.busy || !this.validMessage(true)) return;
      if (this.messages.length >= 20) {
        this.error(this.copy.limit);
        this.showReply();
        return;
      }
      const content = this.body.value.trim();
      const messages = [...this.messages, { role: 'user', content }];
      this.error();
      this.setBusy(true, this.copy.thinking);
      try {
        const result = await this.request({ action: 'message', messages, product: this.querySelector('[data-product]').value });
        if (typeof result.reply !== 'string' || !result.reply.trim() || result.reply.length > 6000) throw new Error('Missing reply');
        this.messages = [...messages, { role: 'assistant', content: result.reply.trim() }];
        this.renderConversation();
        this.body.value = '';
        this.body.required = false;
        this.querySelector('[data-starters]').hidden = true;
        this.setBusy(false, this.copy.replyReady);
        this.body.focus();
      } catch {
        this.setBusy(false);
        this.error(this.copy.unavailable);
      }
    }

    renderConversation() {
      const list = this.querySelector('[data-conversation]');
      list.replaceChildren();
      for (const message of this.messages) {
        const item = document.createElement('li');
        item.dataset.role = message.role;
        const label = document.createElement('strong');
        label.textContent = message.role === 'user' ? this.copy.you : this.copy.aiIdentity;
        const body = document.createElement('p');
        body.textContent = message.content;
        item.append(label, body);
        list.append(item);
      }
      list.hidden = false;
      list.scrollTop = list.scrollHeight;
    }

    enquiryPayload() {
      const content = this.body.value.trim();
      const attachments = this.querySelector('nordsign-file-upload')?.attachments?.() || [];
      return {
        action: 'enquiry',
        ...(attachments.length ? { attachments } : {}),
        messages: content ? [...this.messages, { role: 'user', content }] : this.messages,
        product: this.querySelector('[data-product]').value,
        contact: {
          method: this.phone.disabled ? 'email' : 'phone',
          email: this.email.disabled ? '' : this.email.value.trim(),
          phone: this.phone.disabled ? '' : this.phone.value.trim(),
          name: this.querySelector('#ContactForm-name').value.trim(),
          company: this.querySelector('#ContactForm-company').value.trim(),
          fileReference: this.querySelector('#ContactForm-file-link').value.trim()
        }
      };
    }

    async submit(event) {
      if (this.busy) { event.preventDefault(); return; }
      if (this.querySelector('nordsign-file-upload')?.canSubmit?.() === false) { event.preventDefault(); return; }
      if (this.querySelector('[data-reply]').hidden) {
        event.preventDefault();
        this.showReply();
        return;
      }
      this.setMethod();
      if (!this.phone.disabled && this.phone.value.replace(/\D/g, '').length < 6) this.phone.setCustomValidity(this.copy.phoneRequired);
      if (!this.validMessage() || !this.form.reportValidity()) { event.preventDefault(); return; }
      if (!this.agent) return;
      event.preventDefault();
      const payload = this.enquiryPayload();
      const fingerprint = JSON.stringify(payload);
      if (this.lastFingerprint !== fingerprint) {
        this.requestId = crypto.randomUUID();
        this.lastFingerprint = fingerprint;
      }
      this.error();
      this.setBusy(true, this.copy.sending);
      try {
        const result = await this.request({ ...payload, requestId: this.requestId });
        if (result.accepted !== true || typeof result.reference !== 'string' || !/^[a-z0-9-]{1,80}$/i.test(result.reference)) throw new Error('Unconfirmed receipt');
        this.querySelector('[data-flow]').hidden = true;
        this.querySelector('[data-reference]').textContent = this.copy.reference + result.reference;
        const success = this.querySelector('[data-success]');
        success.hidden = false;
        success.focus();
        this.setBusy(false);
      } catch {
        this.setBusy(false);
        this.error(this.copy.sendError);
      }
    }

    disconnectedCallback() { this.controller?.abort(); }
  }
  customElements.define('nordsign-contact-agent', NordsignContactAgent);
})();
